/**
 * UpSurv 2.0 — QR encoding, locally and only locally (DECISIONS D-013).
 *
 * ── The rule this file exists to keep ───────────────────────────────────────
 * There are QR web services that return a PNG for a URL. Using one here would
 * be wrong twice over, and the second reason is the serious one:
 *
 *   1. It is a network request, and this app is offline-first (D-009). The
 *      office is not a basement, but a build that reaches out for an image is a
 *      build whose onboarding screen goes blank the day that service moves.
 *   2. The URL being encoded CONTAINS A LIVE ONBOARDING SECRET. Handing it to a
 *      third party to draw would put a working credential in somebody else's
 *      access log. That is not a trade-off; it is a disclosure.
 *
 * So the encoding is local, the output is a matrix of booleans, and
 * `src/ui/components/QrCode.tsx` paints it as inline SVG. No image is fetched,
 * no canvas is rasterized, no `<img src>` is built. `tests/e2e/pwa.spec.ts`
 * already asserts a cold offline boot makes zero foreign requests, and this
 * feature keeps that true by construction rather than by care.
 *
 * ── Why a dependency rather than a hand-rolled encoder ──────────────────────
 * `qrcode-generator` is the Kazuhiko Arase implementation: MIT, no dependencies
 * of its own, no DOM and no Node APIs anywhere in it (checked, not assumed), and
 * it bundles into the app like any other module. A QR encoder is Reed-Solomon
 * over GF(256) plus eight mask-penalty rules; a hand-rolled one would be a
 * hundred lines nobody in this repo could verify without a decoder to check it
 * against, which is exactly the kind of code that is subtly wrong for a year.
 *
 * This wrapper exists so the rest of the app never touches that library's
 * imperative API (`addData`/`make`/`isDark`) and never reaches for its
 * `createImgTag` / `createDataURL` helpers, both of which build markup or a
 * data URL out of the secret.
 */

import qrcode from 'qrcode-generator';

/**
 * Error correction level.
 *
 * `M` (~15% recoverable) rather than `L`: the code is scanned off a screen at
 * an angle, in an office, by whatever phone the surveyor happens to own, and a
 * failed scan is a failed onboarding. Higher levels would grow the matrix
 * without buying anything at these URL lengths.
 */
const ERROR_CORRECTION = 'M';

/**
 * A rendered QR code, as data.
 *
 * `modules[row][col]` is true where a dark module goes. `size` is the module
 * count per side (21 for version 1, growing by 4 per version); `quietZone` is
 * the mandatory light margin, in modules, that a scanner needs to find the
 * symbol at all — four is the specification's minimum and leaving it out is the
 * single most common reason a hand-built QR will not scan.
 */
export interface QrMatrix {
  size: number;
  quietZone: number;
  modules: boolean[][];
}

/** The specification's minimum quiet zone, in modules. */
export const QR_QUIET_ZONE = 4;

/**
 * Encode a string as a QR matrix.
 *
 * Byte mode with automatic version selection: the onboarding URL is an origin
 * plus a 43-character base64url secret, which is roughly 70–90 bytes and lands
 * around version 5–7 at this correction level. Version is chosen by the
 * encoder rather than pinned, so a longer preview origin cannot silently
 * overflow a fixed capacity.
 *
 * Throws on input the encoder cannot fit at all (an absurdly long URL). That is
 * a programming error rather than a runtime condition, and the caller renders
 * the failure instead of showing an empty box.
 */
export function qrMatrix(text: string): QrMatrix {
  if (text.length === 0) throw new Error('qrMatrix: nothing to encode');

  // 0 = choose the smallest version that fits.
  const code = qrcode(0, ERROR_CORRECTION);
  code.addData(text, 'Byte');
  code.make();

  const size = code.getModuleCount();
  const modules: boolean[][] = [];
  for (let row = 0; row < size; row++) {
    const line: boolean[] = [];
    for (let col = 0; col < size; col++) line.push(code.isDark(row, col));
    modules.push(line);
  }

  return { size, quietZone: QR_QUIET_ZONE, modules };
}

/**
 * The matrix as one SVG path `d` — every dark module a 1x1 rectangle.
 *
 * One path rather than N rects: a version-6 code is 41x41, so up to 1681
 * elements, and a single path draws identically for a fraction of the DOM. The
 * `h1v1h-1z` form closes each square explicitly so the default nonzero
 * fill-rule cannot punch a hole where two squares meet diagonally.
 *
 * Coordinates are offset by the quiet zone, so the path is drawn inside a
 * viewBox of `size + 2 * quietZone`.
 */
export function qrPath(matrix: QrMatrix): string {
  const parts: string[] = [];
  for (let row = 0; row < matrix.size; row++) {
    const line = matrix.modules[row];
    if (line === undefined) continue;
    for (let col = 0; col < matrix.size; col++) {
      if (line[col] !== true) continue;
      parts.push(`M${String(col + matrix.quietZone)} ${String(row + matrix.quietZone)}h1v1h-1z`);
    }
  }
  return parts.join('');
}

/** The viewBox side length: the symbol plus its quiet zone on both sides. */
export function qrViewBoxSize(matrix: QrMatrix): number {
  return matrix.size + matrix.quietZone * 2;
}
